import { DateTime } from "luxon";

/**
 * Parses an order-related date value (Date object or ISO string) anchored to UTC.
 *
 * Order dates coming from the backend represent a calendar date recorded in the source
 * system, not a moment the customer experienced locally. Converting them to the
 * viewer's browser timezone can roll the calendar date backward or forward (e.g. an
 * order recorded at 2:30 AM UTC displays as the previous day for anyone west of UTC),
 * which previously caused "Order Placed", "Ordered On", and "Order Date" to disagree
 * for the same order. Anchoring every order-date parse to UTC keeps the displayed date
 * consistent everywhere, regardless of the viewer's timezone.
 */
export function parseOrderDate(
  value: Date | string | null | undefined,
): DateTime | null {
  if (!value) return null;

  const dt =
    value instanceof Date
      ? DateTime.fromJSDate(value, { zone: "utc" })
      : DateTime.fromISO(value, { zone: "utc" });

  return dt.isValid ? dt : null;
}

/**
 * Formats a date value to locale display format: M/dd/yyyy (e.g. 6/09/2026).
 * Accepts a Date object, ISO string, or null/undefined.
 * Returns "—" for falsy/invalid input.
 */
export function formatOrderDate(value: Date | string | null | undefined): string {
  const dt = parseOrderDate(value);

  if (!dt) return typeof value === "string" && value ? value : "—";

  return dt.toFormat("D");
}

/**
 * Formats a date with day-of-week and time: EEE, M/dd/yyyy h:mm a (e.g. Mon, 6/09/2026 2:30 PM).
 * Accepts a Date object, ISO string, or null/undefined.
 * Returns null for falsy/invalid input.
 */
export function formatOrderDateTime(value: Date | string | null | undefined): string | null {
  const dt = parseOrderDate(value);

  return dt ? dt.toFormat("EEE, M/dd/yyyy h:mm a") : null;
}

